import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { ModalContext } from "dash-ui";
import { useGetBrokersQuery } from "gql/generated.graphql";
import moment from "moment";
import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  FiChevronDown,
  FiChevronUp,
  FiEdit2,
  FiLock,
  FiMoreHorizontal,
  FiSearch,
  FiUnlock,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiLoader,
} from "react-icons/fi";
import { useImmer } from "use-immer";

// Types

type QueryVariables = {
  filter: {
    isRealUser: Array<boolean>;
    subscriptionPlanTypes: Array<string>;
    licenseStatuses: Array<string>;
    searchText: string | null;
    isSeeker: boolean;
  };
  pagination: {
    skip: number;
    limit: number;
  };
  sort: {
    fieldName: string;
    sortOrder: number;
  } | null;
};

type DeleteBrokerProps = {
  id: string;
  firstName: string;
  lastName?: string | null;
};

type FilterOption = {
  label: string;
  value: string | boolean;
  checked: boolean;
};

// Main Component
export const AgentsTable: FunctionComponent = () => {
  // React states
  const [searchText, setSearchText] = useState<string>("");

  // Contexts
  const { toggleModal, setPayload } = useContext(ModalContext);

  // Pre-determined options with use immer

  const [subscriptionsOptions, setSubscriptionsOptions] = useImmer<
    FilterOption[]
  >([
    { label: "Pro", value: "PRO", checked: false },
    { label: "Basic", value: "Basic", checked: false },
    { label: "Exchange", value: "DASH Exchange", checked: false },
  ]);
  const [userTypeOptions, setUserTypeOptions] = useImmer<FilterOption[]>([
    { label: "Agents", value: true, checked: false },
    { label: "Test", value: false, checked: false },
  ]);
  const [licenseStatusesOptions, setLicenseStatusesOptions] = useImmer<
    FilterOption[]
  >([
    { label: "Active", value: "ACTIVE", checked: false },
    { label: "Inactive", value: "INACTIVE", checked: false },
    { label: "Pending Review", value: "PENDING_REVIEW", checked: false },
    { label: "Missing License", value: "MISSING_LICENSE", checked: false },
  ]);
  const limitOptions = [12, 24, 36, 72];
  const isSeeker = false;
  // Query variable state with use immer
  const [queryVariables, setQueryVariables] = useImmer<QueryVariables>({
    filter: {
      isRealUser: [],
      subscriptionPlanTypes: [],
      licenseStatuses: [],
      searchText: null,
      isSeeker,
    },
    pagination: {
      skip: 0,
      limit: 12,
    },
    sort: null,
  });

  // Get Brokers Query to fill table
  const getBrokersQuery = useGetBrokersQuery({ variables: queryVariables });

  // Derived states for pagination

  const lastPage = Math.ceil(
    (getBrokersQuery.data?.getBrokers.totalCount ||
      queryVariables.pagination.limit) / queryVariables.pagination.limit
  );
  const allPages =
    new Array(lastPage || 1).fill(null).map((_, index) => index + 1) || [];
  const selectedPage =
    queryVariables.pagination.skip / queryVariables.pagination.limit + 1;
  const previousPages = allPages.slice(0, selectedPage - 1);
  const nextPages = allPages.slice(selectedPage, allPages.length);
  const filteredPreviousPages =
    nextPages.length < 3
      ? previousPages.slice(-6 + nextPages.length)
      : previousPages.slice(-3);
  const filteredNextPages =
    previousPages.length < 3
      ? nextPages.slice(0, 6 - previousPages.length)
      : nextPages.slice(0, 3);
  const pages = [...filteredPreviousPages, selectedPage, ...filteredNextPages];

  // Order Brokers Functionality
  const orderBrokers = useCallback((value: string) => {
    setQueryVariables((draft) => {
      const alreadySorted = draft.sort?.fieldName === value;
      const currentOrder = draft.sort?.sortOrder;
      const newOrder = alreadySorted && currentOrder ? currentOrder * -1 : 1;
      draft.sort = { fieldName: value, sortOrder: newOrder };
      localStorage.setItem(
        "DASH_CMS_BROKERS_TABLE_FILTERS",
        JSON.stringify(draft)
      );
    });
  }, []);

  // Filters functions

  const onFilterByName = useCallback((value: string) => {
    setSearchText(value);
    const searchVariable = value.length < 3 ? null : value;
    setQueryVariables((draft) => {
      draft.filter.searchText = searchVariable;
      draft.pagination.skip = 0;
      localStorage.setItem(
        "DASH_CMS_BROKERS_TABLE_FILTERS",
        JSON.stringify(draft)
      );
    });
  }, []);

  const onFilterByPlan = useCallback((value: string) => {
    setQueryVariables((draft) => {
      const alreadyFilteredIndex = draft.filter.subscriptionPlanTypes.findIndex(
        (item) => item === value
      );
      if (alreadyFilteredIndex < 0)
        draft.filter.subscriptionPlanTypes.push(value);
      else draft.filter.subscriptionPlanTypes.splice(alreadyFilteredIndex, 1);
      draft.pagination.skip = 0;
      localStorage.setItem(
        "DASH_CMS_BROKERS_TABLE_FILTERS",
        JSON.stringify(draft)
      );
    });
    setSubscriptionsOptions((draft) => {
      const option = draft.find((item) => item.value === value);
      if (option) option.checked = !option.checked;
    });
  }, []);

  const onFilterByType = useCallback((value: boolean) => {
    setQueryVariables((draft) => {
      const alreadyFilteredIndex = draft.filter.isRealUser.findIndex(
        (item) => item === value
      );
      if (alreadyFilteredIndex < 0) draft.filter.isRealUser.push(value);
      else draft.filter.isRealUser.splice(alreadyFilteredIndex, 1);
      draft.pagination.skip = 0;
      localStorage.setItem(
        "DASH_CMS_BROKERS_TABLE_FILTERS",
        JSON.stringify(draft)
      );
    });
    setUserTypeOptions((draft) => {
      const option = draft.find((item) => item.value === value);
      if (option) option.checked = !option.checked;
    });
  }, []);

  const onFilterByLicenseStatus = useCallback((value: string) => {
    setQueryVariables((draft) => {
      const alreadyFilteredIndex = draft.filter.licenseStatuses.findIndex(
        (item) => item === value
      );
      if (alreadyFilteredIndex < 0) draft.filter.licenseStatuses.push(value);
      else draft.filter.licenseStatuses.splice(alreadyFilteredIndex, 1);
      draft.pagination.skip = 0;
      localStorage.setItem(
        "DASH_CMS_BROKERS_TABLE_FILTERS",
        JSON.stringify(draft)
      );
    });
    setLicenseStatusesOptions((draft) => {
      const option = draft.find((item) => item.value === value);
      if (option) option.checked = !option.checked;
    });
  }, []);

  const onSelectLimit = useCallback((value: number) => {
    setQueryVariables((draft) => {
      draft.pagination.limit = value;
      draft.pagination.skip = 0;
      localStorage.setItem(
        "DASH_CMS_BROKERS_TABLE_FILTERS",
        JSON.stringify(draft)
      );
    });
  }, []);

  // Modals handlers

  const onBlockBroker = async (id: string) => {
    setPayload({ brokerId: id });
    toggleModal(true, "BLOCK_BROKER_MODAL");
  };

  const onUnblockBroker = async (id: string) => {
    setPayload({ brokerId: id });
    toggleModal(true, "UNBLOCK_BROKER_MODAL");
  };

  const onDeleteBroker = async (props: DeleteBrokerProps) => {
    setPayload({
      brokerId: props.id,
      userName: props.lastName
        ? `${props.firstName} ${props.lastName}`
        : props.firstName,
    });
    toggleModal(true, "DELETE_BROKER_MODAL");
  };

  // Pagination functions

  const onNextPage = useCallback(() => {
    if (selectedPage === lastPage) return;
    setQueryVariables((draft) => {
      draft.pagination.skip = draft.pagination.skip + draft.pagination.limit;
      localStorage.setItem(
        "DASH_CMS_BROKERS_TABLE_FILTERS",
        JSON.stringify(draft)
      );
    });
  }, [selectedPage, lastPage]);

  const onPreviousPage = useCallback(() => {
    if (selectedPage === 1) return;
    setQueryVariables((draft) => {
      draft.pagination.skip = draft.pagination.skip - draft.pagination.limit;
      localStorage.setItem(
        "DASH_CMS_BROKERS_TABLE_FILTERS",
        JSON.stringify(draft)
      );
    });
  }, [selectedPage]);

  const onGoToPage = useCallback((page: number) => {
    setQueryVariables((draft) => {
      draft.pagination.skip =
        page * draft.pagination.limit - draft.pagination.limit;
      localStorage.setItem(
        "DASH_CMS_BROKERS_TABLE_FILTERS",
        JSON.stringify(draft)
      );
    });
  }, []);

  // Use Effect to manage filter state on local storage
  useEffect(() => {
    // Check saved filter
    const localFilter = localStorage.getItem("DASH_CMS_BROKERS_TABLE_FILTERS");
    if (!localFilter) {
      localStorage.setItem(
        "DASH_CMS_BROKERS_TABLE_FILTERS",
        JSON.stringify(queryVariables)
      );
      return;
    }
    const parsedLocalFilter = JSON.parse(localFilter) as QueryVariables;

    // Reset values if local filters is corrupted
    if (
      !parsedLocalFilter.filter.isRealUser ||
      !parsedLocalFilter.filter.licenseStatuses ||
      !parsedLocalFilter.filter.subscriptionPlanTypes ||
      !parsedLocalFilter.pagination
    ) {
      localStorage.setItem(
        "DASH_CMS_BROKERS_TABLE_FILTERS",
        JSON.stringify(queryVariables)
      );
      return;
    }

    // Set query variable state with local filter
    setQueryVariables({
      ...parsedLocalFilter,
      filter: {
        ...parsedLocalFilter.filter,
        searchText: null,
        isSeeker,
      },
    });

    // Set filters checked
    setSubscriptionsOptions((draft) => {
      draft.forEach((item) => {
        if (
          parsedLocalFilter.filter.subscriptionPlanTypes.includes(
            item.value as string
          )
        )
          item.checked = true;
      });
    });
    setUserTypeOptions((draft) => {
      draft.forEach((item) => {
        if (parsedLocalFilter.filter.isRealUser.includes(item.value as boolean))
          item.checked = true;
      });
    });
    setLicenseStatusesOptions((draft) => {
      draft.forEach((item) => {
        if (
          parsedLocalFilter.filter.licenseStatuses.includes(
            item.value as string
          )
        )
          item.checked = true;
      });
    });
  }, []);

  // Return HTML component
  return (
    <div className="flex flex-col py-6 w-full gap-6 overflow-x-auto">
      <header className="flex items-center gap-4 px-6">
        <div className="relative flex items-center w-full">
          <input
            type="search"
            className="h-10 px-6 pl-10 border border-slate-400 rounded-lg w-full"
            placeholder="Search Agents"
            onChange={(event) => onFilterByName(event.target.value)}
            value={searchText}
          />
          <FiSearch className="absolute text-slate-400 ml-3" />
        </div>

        <div className="relative">
          <Listbox onChange={(value) => onFilterByPlan(value)}>
            <ListboxButton className="flex items-center h-10 px-6 pr-8 rounded-lg border border-slate-400 min-w-max text-sm relative">
              Subscription Type{" "}
              {queryVariables.filter.subscriptionPlanTypes.length
                ? `(${queryVariables.filter.subscriptionPlanTypes.length})`
                : ""}
              <FiChevronDown className="text-slate-800 text-base absolute right-2" />
            </ListboxButton>

            <ListboxOptions className="absolute flex flex-col mt-1 bg-white border border-slate-300 rounded-lg shadow-xl min-w-full w-max py-2 gap-2">
              {subscriptionsOptions.map((item) => (
                <ListboxOption
                  className="px-4 h-10 flex items-center cursor-pointer hover:bg-sky-50 gap-2 text-slate-800"
                  value={item.value}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={item.checked}
                  />
                  {item.label}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
        </div>

        <div className="relative">
          <Listbox onChange={(value) => onFilterByType(!!value)}>
            <ListboxButton className="flex items-center h-10 px-6 pr-8 rounded-lg border border-slate-400  w-max text-sm relative">
              Account Type{" "}
              {queryVariables.filter.isRealUser.length
                ? `(${queryVariables.filter.isRealUser.length})`
                : ""}
              <FiChevronDown className="text-slate-800 text-base absolute right-2" />
            </ListboxButton>

            <ListboxOptions className="absolute flex flex-col mt-1 bg-white border border-slate-300 rounded-lg shadow-xl min-w-full w-max py-2 gap-2">
              {userTypeOptions.map((item) => (
                <ListboxOption
                  className="px-4 h-10 flex items-center cursor-pointer hover:bg-sky-50 gap-2 text-slate-800"
                  value={item.value}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={item.checked}
                  />
                  {item.label}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
        </div>

        <div className="relative">
          <Listbox onChange={(value) => onFilterByLicenseStatus(value)}>
            <ListboxButton className="flex items-center h-10 px-6 pr-8 rounded-lg border border-slate-400  w-max text-sm relative">
              License Status{" "}
              {queryVariables.filter.licenseStatuses.length
                ? `(${queryVariables.filter.licenseStatuses.length})`
                : ""}
              <FiChevronDown className="text-slate-800 text-base absolute right-2" />
            </ListboxButton>

            <ListboxOptions className="absolute flex flex-col mt-1 bg-white border border-slate-300 rounded-lg shadow-xl min-w-full w-max py-2 gap-2">
              {licenseStatusesOptions.map((item) => (
                <ListboxOption
                  className="px-4 h-10 flex items-center cursor-pointer hover:bg-sky-50 gap-2 text-slate-800"
                  value={item.value}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={item.checked}
                  />
                  {item.label}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
        </div>

        <div className="relative">
          <Listbox onChange={(value) => onSelectLimit(Number(value))}>
            <ListboxButton className="flex items-center h-10 px-6 pr-8 rounded-lg border border-slate-400 min-w-max text-sm relative">
              Show {queryVariables.pagination.limit}
              <FiChevronDown className="text-slate-800 text-base absolute right-2" />
            </ListboxButton>

            <ListboxOptions className="absolute flex flex-col mt-1 bg-white border border-slate-300 rounded-lg shadow-xl min-w-full w-max py-2 gap-2">
              {limitOptions.map((item) => (
                <ListboxOption
                  className="px-4 h-10 flex items-center cursor-pointer hover:bg-sky-50 gap-2 text-slate-800"
                  value={item}
                >
                  {item}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
        </div>
      </header>

      <table className="text-slate-600">
        <thead>
          <tr className="text-slate-400 text-sm h-10">
            <th className="text-left font-normal pl-6 pr-2">
              <button
                className="flex items-center gap-1"
                onClick={() => orderBrokers("profile.firstName")}
              >
                <span>Full Name</span>
                <span className="text-slate-600">
                  {queryVariables.sort?.fieldName === "profile.firstName" &&
                  queryVariables.sort?.sortOrder === -1 ? (
                    <FiChevronDown />
                  ) : (
                    <FiChevronUp />
                  )}
                </span>
              </button>
            </th>

            <th className="text-left font-normal px-2">Email</th>

            <th className="text-left font-normal px-2">Sub. Plan</th>

            <th className="text-left font-normal px-2">
              <button
                className="flex items-center gap-1"
                onClick={() => orderBrokers("license.licenseStatus")}
              >
                <span>License Status</span>
                <span className="text-slate-600">
                  <FiChevronDown />
                </span>
              </button>
            </th>

            <th className="text-left font-normal px-2">
              <button
                className="flex items-center gap-1"
                onClick={() => orderBrokers("account.createdAt")}
              >
                <span>Created Date</span>
                <span className="text-slate-600">
                  {queryVariables.sort?.fieldName === "account.createdAt" &&
                  queryVariables.sort?.sortOrder === -1 ? (
                    <FiChevronDown />
                  ) : (
                    <FiChevronUp />
                  )}
                </span>
              </button>
            </th>

            <th className="text-center font-normal pl-2 pr-6">Actions</th>
          </tr>
        </thead>

        <tbody className="">
          {getBrokersQuery.loading && (
            <tr>
              <td className="animate-pulse bg-slate-50" colSpan={6}>
                <div className="flex items-center justify-center h-40 text-sm text-slate-500 gap-2">
                  <FiLoader className="animate-spin h-4 w-4" />
                  <span>Loading...</span>
                </div>
              </td>
            </tr>
          )}

          {getBrokersQuery.error && (
            <tr>
              <td className="bg-slate-50" colSpan={6}>
                <div className="flex items-center justify-center h-40 text-sm text-slate-600 gap-2">
                  <div className="bg-red-50 flex gap-2 items-center justify-center rounded-lg border border-red-600 text-red-800 h-10 px-5">
                    <FiX className=" h-4 w-4" />
                    <span>Something went wrong, please refresh the page.</span>
                  </div>
                </div>
              </td>
            </tr>
          )}

          {getBrokersQuery.data?.getBrokers.collection.map((broker) => (
            <tr className="h-14 even:bg-white odd:bg-slate-50">
              <td className="pl-6 pr-2">
                <a href={`/brokers/${broker._id}`} target="_blank">
                  {broker.profile.lastName
                    ? `${broker.profile.firstName} ${broker.profile.lastName}`
                    : broker.profile.firstName}
                </a>
              </td>

              <td className="px-2">{broker.account.email}</td>

              <td className="px-2">
                {broker.accountSubscription?.subscriptionProduct.name}
              </td>

              <td className={`px-2`}>
                {broker.license?.licenseStatus === "ACTIVE" && (
                  <span className="bg-green-100 text-green-700 text-sm px-2 py-px rounded-lg-sm">
                    Active
                  </span>
                )}
                {broker.license?.licenseStatus === "INACTIVE" && (
                  <span className="bg-red-100 text-red-700 text-sm px-2 py-px rounded-lg-sm">
                    Inactive
                  </span>
                )}
                {broker.license?.licenseStatus === "PENDING_REVIEW" && (
                  <span className="bg-yellow-100 text-yellow-700 text-sm px-2 py-px rounded-lg-sm">
                    Pending Review
                  </span>
                )}
                {!broker.license?.licenseStatus && (
                  <span className="bg-slate-200 text-sm px-2 py-px rounded-lg-sm">
                    Missing license
                  </span>
                )}
              </td>

              <td className="px-2">
                {moment(broker.account.createdAt).format("MM/DD/yyyy")}
              </td>

              <td className="pr-6 pl-2 text-center">
                <Menu>
                  <MenuButton className="h-10 px-4 text-center hover:bg-sky-600/5 hover:text-sky-600 rounded-lg">
                    <FiMoreHorizontal />
                  </MenuButton>

                  <MenuItems
                    anchor="bottom end"
                    className="flex flex-col bg-white rounded-lg-lg shadow-xl border py-2 text-slate-700 text-sm"
                  >
                    <MenuItem>
                      <a
                        href={`/brokers/${broker._id}`}
                        target="_blank"
                        className="flex items-center gap-2 px-5 h-10 hover:bg-sky-50 text-slate-700"
                      >
                        {" "}
                        <FiEdit2 /> View / Edit
                      </a>
                    </MenuItem>

                    {broker.account.status === "ARCHIVED" ? (
                      <MenuItem>
                        <button
                          className="px-5 h-10 hover:bg-sky-50"
                          onClick={() => onUnblockBroker(broker._id)}
                        >
                          <span className="flex items-center gap-2">
                            <FiUnlock /> Unblock
                          </span>
                        </button>
                      </MenuItem>
                    ) : (
                      <MenuItem>
                        <button
                          className="px-5 h-10 hover:bg-sky-50"
                          onClick={() => onBlockBroker(broker._id)}
                        >
                          <span className="flex items-center gap-2">
                            <FiLock /> Block
                          </span>
                        </button>
                      </MenuItem>
                    )}

                    <MenuItem>
                      <button
                        className="flex items-center gap-2 px-5 h-10 hover:bg-sky-50"
                        onClick={() =>
                          onDeleteBroker({
                            id: broker._id,
                            firstName: broker.profile.firstName,
                            lastName: broker.profile.lastName,
                          })
                        }
                      >
                        <FiX /> Delete
                      </button>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <footer className="flex items-center px-6 justify-between">
        <p className="text-slate-400 text-sm">
          {`Showing ${queryVariables.pagination.skip + 1} - ${
            queryVariables.pagination.skip +
            (getBrokersQuery.data?.getBrokers.collection.length ||
              queryVariables.pagination.skip)
          } of ${getBrokersQuery.data?.getBrokers.totalCount} Agents`}
        </p>

        <div className="flex gap-1">
          <button
            disabled={selectedPage === 1}
            className="disabled:opacity-50 h-8 w-8 flex items-center justify-center rounded-lg border border-slate-300 text-sm text-sky-600"
            onClick={onPreviousPage}
          >
            <FiChevronLeft />
          </button>

          {pages.map((page) => {
            return (
              <button
                onClick={() => onGoToPage(page)}
                className={`h-8 w-8 flex items-center text-sky-600 justify-center rounded-lg text-sm ${
                  selectedPage === page
                    ? "bg-sky-600 text-white font-semibold"
                    : "border border-slate-300"
                }`}
              >
                {page}
              </button>
            );
          })}

          <button
            disabled={selectedPage === lastPage}
            className="disabled:opacity-50 h-8 w-8 flex items-center justify-center rounded-lg border border-slate-300 text-sm text-sky-600"
            onClick={onNextPage}
          >
            <FiChevronRight />
          </button>
        </div>
      </footer>
    </div>
  );
};
